import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EnvironmentService } from './environment.service';
import { StorageManagerService } from './storage-manager.service';
import { UserInfo } from '../models';
import { Router } from '@angular/router';
import { RemoteConfig } from '../models/remote-config';
import { RemoteConfigService } from './remote-config.service';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AuthResponseV2, RenewJwtSession } from '../models/auth-response.v2';
import { environment } from '../../environments/environment';
import { DeviceManagementService } from './device-management.service';


@Injectable({
    providedIn: 'root'
})
export class AccountService {
    nextRoute: string;
    goHome = false;
    renewingSession: boolean = false;
    private _isAuthenticated = new BehaviorSubject<boolean>(false);

    public isAuthenticated(): boolean {
        return this._isAuthenticated.value;
    }

    public get isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated.asObservable();
    }

    constructor(private http: HttpClient,
                private envService: EnvironmentService,
                private storageManager: StorageManagerService,
                private router: Router,
                private config: RemoteConfigService,
                private deviceManagement: DeviceManagementService) {
        const accessToken = this.config.remoteConfig.login_version == 2 ? storageManager.getAuthTokenV2()?.token : storageManager.getToken();
        this.setAuthenticated(!!accessToken && accessToken !== 'null' && accessToken.trim() !== '');
    }

    public setAuthenticated(authenticated: boolean): void {
        this._isAuthenticated.next(authenticated);
    }

    public checkUnauthorized(error) {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
                this.logoutUser();
            }
        }
    }

    getRecaptchaToken(action) {
        return new Promise<string>(resolve => {
            window['grecaptcha']?.enterprise?.ready(() => {
                window['grecaptcha']?.enterprise?.execute(environment.recaptchaKey, { action: action }).then((token) => {
                    resolve(token);
                });
            });
        })
    }

    public logoutUser() {
        this.storageManager.removeSubscription();
        this.storageManager.removeToken();
        this.storageManager.removeEmail();
        this.storageManager.removeAuthTokenV2();
        this.setAuthenticated(false);
        this.router.navigate(['/login']);
    }

    getUserInfoQuery(querySelect: string): Observable<UserInfo> {
        const query = querySelect.split(',');

        if (!this.config.remoteConfig.points_enabled && query.includes('points')) {
            query.splice(query.indexOf('points'), 1);
        }

        if (!this.config.remoteConfig.patreon_enabled && query.includes('patreon')) {
            query.splice(query.indexOf('patreon'), 1);
        }

        return this.http.get<UserInfo>(this.envService.userHost + '/v1/userinfo/query?select=' + query.join(','));
    }

    renewSmartTVSession(hash: string): Observable<any> {
        return this.http.get<any>(`${this.envService.userHost}/v1/userdevice/${hash}/renew`, { observe: 'response' });
    }

    getSTVCode(hash: string) {
        return this.http.get<{ code: string }>(`${this.envService.userHost}/v2/smartlogin/${hash}`);
    }

    checkLogin(hash: string, code: string) {
        return this.http.get<any>(`${this.envService.userHost}/v2/smartlogin/${hash}/${code}`, { observe: 'response' });
    }

    async updateToken(): Promise<void> {
        if (this.config.remoteConfig.login_version == 1) {
            await this.updateTokenV1();
        }
        if (this.config.remoteConfig.login_version == 2) {
            if (this.isAuthenticated()) {
                return Promise.resolve();
            }
            return Promise.reject();
        }
    }

    async updateTokenV1(): Promise<void> {
        const expiryDate = new Date(this.storageManager.getExpiry());
        const currentDate = new Date();

        if (this.tokenExpired(expiryDate, currentDate)) {
            this.logoutUser();
            return;
        }

        if (this.shouldRenewToken(expiryDate, currentDate)) {
            let deviceHash = this.storageManager.getDeviceHash();

            try {
                if (!deviceHash) {
                    throw new Error('No Device Hash');
                }
                let response = await this.renewSmartTVSession(deviceHash).toPromise();
                this.storageManager.setToken(response.body.token);
                this.storageManager.setExpiry(response.body.expiry);
            } catch (e) {
                this.logoutUser();
                throw e;
            }
        }
    };

    sessionRenew$: Observable<string> = new Observable<string>();
    public updateTokenV2(): Observable<string> {

        // Not logged in
        if (!this.isAuthenticated()) {
            console.log('No session');
            return of(null);
        }

        const session = this.storageManager.getAuthTokenV2();

        // session not found
        if (!session) {
            console.log('No session');
            return of(null);
        }

        const now = Date.now();

        // Logged in but session and refresh token expired
        if (new Date(session.refreshExpiry).getTime() < now) {
            console.log('Refresh Expired');
            return of(null);
        }

        // Logged in and session expired but can refresh
        if (new Date(session.expiry).getTime() - (3 * 24 * 60 * 60 * 1000) < now) {
            console.log('Session Expired');
            if (!this.renewingSession) {
                console.log('Refreshing Token');
                this.sessionRenew$ = this.renewV2(session).pipe(shareReplay(1), map(x => x.token));
            }
            return this.sessionRenew$;
        }
        return of(session.token);
    }

    renewV2(jwtSession: RenewJwtSession): Observable<AuthResponseV2> {
        this.renewingSession = true;
        jwtSession.device = this.deviceManagement.device;
        return this.http.post<AuthResponseV2>(`${this.envService.userHost}/v2/account/renew?platform=web`, jwtSession)
            .pipe(
                map(x => {
                    this.storageManager.setAuthTokenV2(x);
                    this.renewingSession = false;
                    return x;
                }),
                catchError((e) => {
                    console.log(e);
                    this.storageManager.removeAuthTokenV2();
                    this.setAuthenticated(false);
                    throw e;
                }));
    }

    /**
     * Should be renewed if expiry < 5 days.
     * @param expiryDate
     * @param currentDate
     * @returns
     */
    private shouldRenewToken(expiryDate: Date, currentDate: Date): boolean {
        let timeDifference = expiryDate.getTime() - currentDate.getTime();
        return Math.ceil(timeDifference / (1000 * 60 * 60 * 24)) < 5;
    }

    /**
     * Checks if the token has expired.
     * @param expiryDate
     * @param currentDate
     * @returns
     */
    private tokenExpired(expiryDate: Date, currentDate: Date): boolean {
        return expiryDate < currentDate;
    }
}
